import { IUser } from "../models/IUser";
import { AdminRole } from "../types/adminRoles";

/**
 * Check if user has any admin roles (excluding referee admin, head coach, and court manager)
 */
export const hasAdminAccess = (user: IUser | null): boolean => {
  if (!user) return false;
  if (!user.adminRoles || user.adminRoles.length === 0) return false;

  // Filter out referee admin, head coach, and court manager - they have limited dashboard access
  const validRoles = user.adminRoles.filter((role) => role !== AdminRole.REFEREE_ADMIN);
  return validRoles.length > 0;
};

/**
 * Check if user has superadmin role
 */
export const isSuperAdmin = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.SUPERADMIN) || false;
};

/**
 * Check if user has referee admin role
 */
export const isRefereeAdmin = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.REFEREE_ADMIN) || false;
};

/**
 * Check if user has registration manager role
 */
export const isRegistrationManager = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.REGISTRATION_MANAGER) || false;
};

/**
 * Check if user has bracket manager role
 */
export const isBracketManager = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.BRACKET_MANAGER) || false;
};

/**
 * Check if user has head coach role
 */
export const isHeadCoach = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.HEAD_COACH) || false;
};

/**
 * Check if user has court manager role
 */
export const isCourtManager = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.COURT_MANAGER) || false;
};

/**
 * Check if user has the padel reports viewer role (read-only access to
 * padel tournament reports — aggregates only, no PII).
 */
export const isPadelReportsViewer = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.PADEL_REPORTS_VIEWER) || false;
};

/**
 * True when the user is a PadelReportsViewer and not a Superadmin.
 * Used to lock down the Reports page UI to summary-only.
 */
export const isPadelReportsViewerOnly = (user: IUser | null): boolean => {
  return isPadelReportsViewer(user) && !isSuperAdmin(user);
};

/**
 * Check if user has tournament manager role (scoped to tournament-domain actions).
 */
export const isTournamentManager = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.TOURNAMENT_MANAGER) || false;
};

/**
 * Check if user has the photographer role (gallery upload/organize only).
 */
export const isPhotographer = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.PHOTOGRAPHER) || false;
};

/**
 * Check if user has the shop manager role (shop products and orders only).
 */
export const isShopManager = (user: IUser | null): boolean => {
  if (!user) return false;
  return user.adminRoles?.includes(AdminRole.SHOP_MANAGER) || false;
};

/**
 * Check if user is ONLY a bracket manager (no other admin roles)
 */
export const isOnlyBracketManager = (user: IUser | null): boolean => {
  if (!user) return false;
  if (!user.adminRoles || user.adminRoles.length === 0) return false;

  // Filter out referee admin since it doesn't count for dashboard access
  const validRoles = user.adminRoles.filter((role) => role !== AdminRole.REFEREE_ADMIN);

  // User is only bracket manager if they have exactly one valid role and it's bracket manager
  return validRoles.length === 1 && validRoles[0] === AdminRole.BRACKET_MANAGER;
};

/**
 * Check if user can access home/dashboard
 */
export const canAccessHome = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin can access home
};

/**
 * Check if user can access tournaments page
 */
export const canAccessTournaments = (user: IUser | null): boolean => {
  return (
    isSuperAdmin(user) ||
    isRegistrationManager(user) ||
    isBracketManager(user) ||
    isTournamentManager(user)
  );
};

/**
 * Check if user can access players page
 */
export const canAccessPlayers = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin
};

/**
 * Check if user can access representers page
 */
export const canAccessRepresenters = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin
};

/**
 * Check if user can access user management page
 */
export const canAccessUserManagement = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin
};

/**
 * Check if user can add/edit/delete tournaments
 */
export const canManageTournaments = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user);
};

/**
 * Check if user can access tournament galleries (the Galleries list page and any
 * tournament's gallery). Photographers get this as their only access; tournament
 * managers and superadmins keep it too (they also reach galleries via Tournaments).
 */
export const canAccessGalleries = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user) || isPhotographer(user);
};

/**
 * Check if user can add/edit/delete categories
 */
export const canManageCategories = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user);
};

/**
 * Check if user can update player status
 */
export const canUpdatePlayerStatus = (user: IUser | null): boolean => {
  return hasAdminAccess(user); // All admin roles can update player status
};

/**
 * Check if user can access arena tab
 */
export const canAccessArena = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isBracketManager(user) || isTournamentManager(user);
};

/**
 * Check if user can access teams tab
 */
export const canAccessTeamsTab = (user: IUser | null): boolean => {
  return hasAdminAccess(user) || isRegistrationManager(user);
};

/**
 * Check if user can access players tab
 */
export const canAccessPlayersTab = (user: IUser | null): boolean => {
  return hasAdminAccess(user) || isRegistrationManager(user);
};

/**
 * Check if user can access category details tab
 */
export const canAccessDetailsTab = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user);
};

/**
 * Check if user can complete brackets
 */
export const canCompleteBracket = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isBracketManager(user) || isTournamentManager(user);
};

/**
 * Check if user can finish an Americano stage early.
 * Mirrors the backend MexicanoRunPolicy (RefereeAdmin, BracketManager, TournamentManager, Superadmin).
 */
export const canFinishAmericano = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isBracketManager(user) || isTournamentManager(user) || isRefereeAdmin(user);
};

/**
 * Check if user can create schedules
 */
export const canCreateSchedule = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user);
};

/**
 * Check if user can delete schedules
 */
export const canDeleteSchedule = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user);
};

/**
 * Check if user can delete stages
 */
export const canDeleteStage = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isTournamentManager(user);
};

/**
 * Check if user can access court reservations
 */
export const canAccessCourtReservations = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isHeadCoach(user) || isCourtManager(user); // All authenticated admin users, head coach, and court manager can access court reservations
};

/**
 * Check if user can manage courts (add, update, delete)
 */
export const canManageCourts = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isCourtManager(user); // Superadmin and court manager can manage courts
};

/**
 * Check if user can manage seasons (add, update, delete)
 */
export const canManageSeasons = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin can manage seasons
};

/**
 * Check if user can manage locations (add, update, delete)
 */
export const canManageLocations = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin can manage locations
};

/**
 * Check if user can manage training schedules (add, edit, delete)
 */
export const canManageTrainingSchedules = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isHeadCoach(user); // All admin users, head coach, and court manager can manage training schedules
};

/**
 * Check if user can create regular reservations (not training schedules)
 */
export const canCreateRegularReservations = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isCourtManager(user) || isHeadCoach(user); // Full admin users, court manager, and head coach can create regular reservations
};

/**
 * Check if user can access transactions page
 */
export const canAccessTransactions = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin can access transactions
};

/**
 * Check if user can access tournament reports page
 */
export const canAccessReports = (user: IUser | null): boolean => {
  // Superadmin sees full reports; PadelReportsViewer gets a sport-gated,
  // PII-redacted view (only the summary cards for padel tournaments).
  return isSuperAdmin(user) || isPadelReportsViewer(user);
};

/**
 * Check if user can manage third-party partner applications (API keys)
 */
export const canManagePartnerApplications = (user: IUser | null): boolean => {
  return isSuperAdmin(user); // Only superadmin can issue/revoke API keys
};

/**
 * Check if user can manage payment accounts (Configuration → Payment Accounts tab).
 */
export const canManagePaymentAccounts = (user: IUser | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can credit/debit a user's Clash Points (admin adjust).
 * Superadmin-only, matching the backend SuperadminPolicy on POST /api/points/adjust.
 */
export const canAdjustClashPoints = (user: IUser | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can manage announcements.
 */
export const canManageAnnouncements = (user: IUser | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can manage the site partners shown on the web/mobile home pages.
 * Superadmin-only, matching the backend SuperadminPolicy on SitePartnerController.
 */
export const canManagePartners = (user: IUser | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can manage the shop (products, stock and orders).
 * Matches the backend ShopManagerPolicy (Superadmin or ShopManager).
 */
export const canManageShop = (user: IUser | null): boolean => {
  return isSuperAdmin(user) || isShopManager(user);
};

/**
 * Check if user can manage events (create/edit/delete).
 */
export const canManageEvents = (user: IUser | null): boolean => {
  return isSuperAdmin(user);
};

/**
 * Check if user can access the Configuration page
 * (seasons / locations / payment accounts / partner applications / site config / expense categories tabs)
 */
export const canAccessConfiguration = (user: IUser | null): boolean => {
  return (
    canManageSeasons(user) ||
    canManageLocations(user) ||
    canManagePaymentAccounts(user) ||
    canManagePartnerApplications(user)
  );
};

/**
 * Get default route based on user role
 */
export const getDefaultRoute = (user: IUser | null): string => {
  // Head coach goes to court reservations
  if (isHeadCoach(user)) {
    return "/dashboard/court-reservations";
  }

  // Court manager goes to court reservations
  if (isCourtManager(user)) {
    return "/dashboard/court-reservations";
  }

  if (!hasAdminAccess(user)) return "/";

  // Superadmin goes to dashboard home
  if (isSuperAdmin(user)) {
    return "/dashboard";
  }

  // Bracket manager, registration manager, and tournament manager go to tournaments
  if (
    isBracketManager(user) ||
    isRegistrationManager(user) ||
    isTournamentManager(user)
  ) {
    return "/dashboard/tournaments";
  }

  // Padel reports viewer (without superadmin) goes straight to Reports —
  // they don't have access to anything else.
  if (isPadelReportsViewerOnly(user)) {
    return "/dashboard/reports";
  }

  // Photographer's only surface is the galleries list.
  if (isPhotographer(user)) {
    return "/dashboard/galleries";
  }

  // Shop manager's only surface is the shop; orders are the day-to-day page.
  if (isShopManager(user)) {
    return "/dashboard/shop/orders";
  }

  // Default to dashboard home
  return "/dashboard";
};
